// lib/fingerprint.ts
// Сбор браузерного фингерпринта для защиты от фрода, VPN/прокси детекции
// Правовая база: legitimate interest (GDPR Art. 6.1.f) — только безопасность
// Хранится как хеш — сырые данные НЕ сохраняются

export interface FingerprintData {
  hash: string;
  canvasHash: string;
  webglHash: string;
  timezone: string;
  language: string;
  screen: string;
  platform: string;
  touchPoints: number;
  colorDepth: number;
  hardwareConcurrency: number;
  deviceMemory: number | null;
  sessionId: string;
  suspiciousSignals: string[];
}

export interface FingerprintRisk {
  score: number;
  level: "low" | "medium" | "high";
  reasons: string[];
}

function fnv1a(str: string): string {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

function getCanvasHash(): string {
  try {
    const c = document.createElement("canvas");
    c.width = 200; c.height = 40;
    const ctx = c.getContext("2d");
    if (!ctx) return "no-canvas";
    ctx.textBaseline = "top";
    ctx.font = "14px Arial";
    ctx.fillStyle = "#f60";
    ctx.fillRect(0, 0, 200, 40);
    ctx.fillStyle = "#069";
    ctx.fillText("Salaria.Security", 2, 5);
    ctx.fillStyle = "rgba(102,204,0,0.7)";
    ctx.fillText("Salaria.Security", 4, 8);
    return fnv1a(c.toDataURL());
  } catch {
    return "canvas-blocked";
  }
}

function getWebGLHash(): string {
  try {
    const c = document.createElement("canvas");
    const gl = c.getContext("webgl") as WebGLRenderingContext | null;
    if (!gl) return "no-webgl";
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    if (!ext) return "no-ext";
    const vendor   = gl.getParameter(ext.UNMASKED_VENDOR_WEBGL)   as string;
    const renderer = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string;
    return fnv1a(`${vendor}::${renderer}`);
  } catch {
    return "webgl-blocked";
  }
}

function getSessionId(): string {
  const KEY = "salaria_sid";
  let sid = sessionStorage.getItem(KEY);
  if (!sid) {
    sid = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
    sessionStorage.setItem(KEY, sid);
  }
  return sid;
}

function detectSuspiciousSignals(data: Omit<FingerprintData, "hash" | "suspiciousSignals" | "sessionId">): string[] {
  const signals: string[] = [];
  if (navigator.webdriver)                                            signals.push("webdriver");
  if (!(window as any).chrome && navigator.userAgent.includes("Chrome"))      signals.push("fake-chrome");
  if (data.canvasHash === "canvas-blocked")                          signals.push("canvas-blocked");
  if (data.webglHash === "no-webgl" || data.webglHash === "no-ext") signals.push("webgl-unavailable");
  if (data.platform === "unknown")                                    signals.push("unknown-platform");
  if (data.hardwareConcurrency === 0)                                signals.push("zero-cpu-cores");
  const [w] = data.screen.split("x").map(Number);
  if (w > 0 && w < 300)                                              signals.push("tiny-screen");
  if (data.timezone === "UTC" && !data.language.startsWith("en"))    signals.push("utc-timezone-mismatch");
  return signals;
}

export async function collectFingerprint(): Promise<FingerprintData> {
  if (typeof window === "undefined") {
    return { hash: "ssr", canvasHash: "ssr", webglHash: "ssr", timezone: "UTC", language: "unknown", screen: "0x0x0", platform: "unknown", touchPoints: 0, colorDepth: 0, hardwareConcurrency: 0, deviceMemory: null, sessionId: "ssr", suspiciousSignals: [] };
  }

  const nav                 = navigator;
  const timezone            = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const language            = nav.language ?? "unknown";
  const screen              = `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`;
  const platform            = nav.platform ?? "unknown";
  const touchPoints         = nav.maxTouchPoints ?? 0;
  const colorDepth          = window.screen.colorDepth;
  const hardwareConcurrency = nav.hardwareConcurrency ?? 0;
  const deviceMemory        = (nav as any).deviceMemory ?? null;
  const canvasHash          = getCanvasHash();
  const webglHash           = getWebGLHash();
  const sessionId           = getSessionId();

  const base = { canvasHash, webglHash, timezone, language, screen, platform, touchPoints, colorDepth, hardwareConcurrency, deviceMemory };
  const suspiciousSignals   = detectSuspiciousSignals(base);
  const hash                = fnv1a([timezone, language, screen, platform, touchPoints, colorDepth, deviceMemory, hardwareConcurrency, canvasHash, webglHash].join("|"));

  return { hash, ...base, sessionId, suspiciousSignals };
}

export function assessRisk(fp: FingerprintData): FingerprintRisk {
  const weights: Record<string, number> = {
    "webdriver": 0.9, "fake-chrome": 0.7, "canvas-blocked": 0.3,
    "webgl-unavailable": 0.2, "unknown-platform": 0.4,
    "zero-cpu-cores": 0.5, "tiny-screen": 0.6, "utc-timezone-mismatch": 0.3,
  };
  let score = 0;
  const reasons: string[] = [];
  for (const signal of fp.suspiciousSignals) {
    score = Math.min(1, score + (weights[signal] ?? 0.1));
    reasons.push(signal);
  }
  const level: FingerprintRisk["level"] = score >= 0.7 ? "high" : score >= 0.35 ? "medium" : "low";
  return { score: Math.round(score * 100) / 100, level, reasons };
}

export function compareFingerprints(a: FingerprintData, b: FingerprintData): number {
  const checks = [
    a.hash === b.hash, a.timezone === b.timezone, a.language === b.language,
    a.platform === b.platform, a.hardwareConcurrency === b.hardwareConcurrency,
    a.colorDepth === b.colorDepth, a.canvasHash === b.canvasHash,
  ];
  return checks.filter(Boolean).length / checks.length;
}